import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';

const ambienteSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  api_key: z.string().min(1, 'API Key é obrigatória'),
  ativo: z.boolean(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

type AmbienteFormData = z.infer<typeof ambienteSchema>;

export const AmbienteForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const { addToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AmbienteFormData>({
    resolver: zodResolver(ambienteSchema),
    defaultValues: {
      ativo: true,
    },
  });

  const createdAt = watch('createdAt');
  const updatedAt = watch('updatedAt');

  useEffect(() => {
    if (isEditing && id) {
      fetchAmbiente(id);
    } else {
      setTimeout(() => {
        document.getElementById('nome')?.focus();
      }, 100);
    }
  }, [id, isEditing]);

  const fetchAmbiente = async (ambienteId: string) => {
    try {
      const response = await api.get<AmbienteFormData>(`/api/ambientes/${ambienteId}`);
      reset(response.data);
    } catch (error: any) {
      console.error('Error fetching ambiente:', error);
      addToast({
        type: 'error',
        title: 'Erro ao carregar ambiente',
        description: error.response?.data?.error || error.message
      });
      navigate('/ambientes');
    }
  };

  const onSubmit = async (data: AmbienteFormData) => {
    const url = isEditing ? `/api/ambientes/${id}` : '/api/ambientes';
    const method = isEditing ? 'put' : 'post';

    try {
      let response;
      if (method === 'put') {
        response = await api.put(url, data);
      } else {
        response = await api.post(url, data);
      }

      if (response.status === 200 || response.status === 201) {
        addToast({
          type: 'success',
          title: `Ambiente ${isEditing ? 'atualizado' : 'criado'} com sucesso!`
        });
        navigate('/ambientes');
      } else {
        console.error('Falha ao salvar ambiente, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Erro ao salvar', description: errorDesc });
        throw new Error(errorDesc || 'Falha ao salvar ambiente');
      }
    } catch (error: any) {
      console.error('Error saving ambiente:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao salvar.';
      addToast({
        type: 'error',
        title: 'Erro ao salvar ambiente',
        description: errorMessage
      });
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch (error) {
      return '-';
    }
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/ambientes')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Editar Ambiente' : 'Novo Ambiente'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
            Nome *
          </label>
          <input
            type="text"
            id="nome"
            {...register('nome')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          {errors.nome && (
            <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="api_key" className="block text-sm font-medium text-gray-700">
            API Key *
          </label>
          <input
            type="text"
            id="api_key"
            {...register('api_key')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          {errors.api_key && (
            <p className="mt-1 text-sm text-red-600">{errors.api_key.message}</p>
          )}
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="ativo"
            {...register('ativo')}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="ativo" className="ml-2 block text-sm text-gray-900">
            Ativo
          </label>
        </div>

        {isEditing && (
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
            <div>
              <p>Criado em: {formatDate(createdAt)}</p>
            </div>
            <div>
              <p>Atualizado em: {formatDate(updatedAt)}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/ambientes')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};